import { Capacitor } from '@capacitor/core';

/**
 * Remote Config 래퍼. 호출 사이트는 한 패턴만 알면 됨:
 *   await RemoteConfig.fetchAndActivate();
 *   const minutes = await RemoteConfig.getNumber('heart_regen_minutes');
 *
 * SDK v8 의 `@capacitor-firebase/remote-config` 는 `setDefaults` API 가 없음 —
 * 대신 in-code DEFAULTS 를 single source of truth 로 두고, 네이티브 getter 가
 * Static source (= 키가 fetched config 에 없음) 를 반환할 때 memDefaults 로
 * fallback. 웹에서는 memDefaults 를 직접 반환해 dev parity 유지.
 *
 * native 진입점 (NativeRemoteConfig) 은 src/services/FirebaseRemoteConfig.ts 로
 * 분리되어 동적 import — 웹 번들에 capacitor-firebase/remote-config 코드가
 * 포함되지 않음. Telemetry/AuthService 와 동일한 패턴.
 */

const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * 정책: DEFAULTS 상수가 모든 키의 단일 source of truth. Firebase Console 의
 * "parameter defaults" 는 의도적으로 무시 — 운영 중에 default 를 바꾸려면 코드를
 * 수정해 새 빌드로 배포. 콘솔에서는 fetch 후 활성화될 "remote value" 만 관리.
 *
 * 이유: 클라이언트가 fetch 전이거나 fetch 실패 상태에서도 동작이 결정적이도록.
 * SDK 가 fetch 전에 반환하는 'static'/'default' 값은 모두 Static 으로 묶이고,
 * 그 경우 memDefaults[key] 로 fallback.
 */
const DEFAULTS = {
  heart_regen_minutes: 60,
  ad_show_probability: 0.6,
  event_codes_enabled: true,
} as const;

const memDefaults: Record<string, string | number | boolean> = { ...DEFAULTS };

let fetched = false;
let activatePromise: Promise<void> | null = null;

/**
 * native 어댑터 — FirebaseRemoteConfig.NativeRemoteConfig 와 매칭.
 * SDK 의 GetValueSource enum 을 단순 boolean (isStatic) 으로 정규화해
 * 이 모듈이 enum 모양을 알 필요 없게 한다.
 */
interface NativeRemoteConfigAdapter {
  fetchAndActivate(): Promise<void>;
  getString(key: string): Promise<{ value: string; isStatic: boolean }>;
  getNumber(key: string): Promise<{ value: number; isStatic: boolean }>;
  getBoolean(key: string): Promise<{ value: boolean; isStatic: boolean }>;
}

let cached: Promise<NativeRemoteConfigAdapter> | null = null;

function getNative(): Promise<NativeRemoteConfigAdapter> {
  if (!cached) {
    cached = import('./FirebaseRemoteConfig')
      .then((m) => {
        // 명시적 타입 assignment — NativeRemoteConfig 시그니처 drift 시
        // 컴파일 타임에 실패.
        const inst: NativeRemoteConfigAdapter = new m.NativeRemoteConfig();
        return inst;
      })
      .catch((err) => {
        cached = null;
        throw err;
      });
  }
  return cached;
}

export async function fetchAndActivate(): Promise<void> {
  if (!isNative()) {
    // 웹에서는 fetch 가 의미 없음 — 단지 fetched 플래그만 세움.
    fetched = true;
    return;
  }
  // in-flight dedupe — 동시 호출 시 native SDK 가 dedupe 안 하므로 race 방지.
  // 동시 호출 중 첫 번째 실패 + 두 번째 성공 시 fetched 가 부분 성공으로 마스킹되는 것도 방지.
  if (activatePromise) return activatePromise;
  activatePromise = (async () => {
    try {
      const n = await getNative();
      await n.fetchAndActivate();
      fetched = true;
    } catch (err) {
      console.warn('RemoteConfig.fetchAndActivate failed', err);
      // fetched = false 유지 — 다음 호출에서 재시도 가능.
    } finally {
      activatePromise = null;
    }
  })();
  return activatePromise;
}

export async function getString(key: string, fallback = ''): Promise<string> {
  if (!isNative()) {
    const v = memDefaults[key];
    return typeof v === 'string' ? v : fallback;
  }
  try {
    const n = await getNative();
    const { value, isStatic } = await n.getString(key);
    if (isStatic) {
      const d = memDefaults[key];
      return typeof d === 'string' ? d : fallback;
    }
    return value;
  } catch (err) {
    console.warn('RemoteConfig.getString failed', key, err);
    return fallback;
  }
}

export async function getNumber(key: string, fallback = 0): Promise<number> {
  if (!isNative()) {
    const v = memDefaults[key];
    return typeof v === 'number' ? v : fallback;
  }
  try {
    const n = await getNative();
    const { value, isStatic } = await n.getNumber(key);
    if (isStatic) {
      const d = memDefaults[key];
      return typeof d === 'number' ? d : fallback;
    }
    return value;
  } catch (err) {
    console.warn('RemoteConfig.getNumber failed', key, err);
    return fallback;
  }
}

export async function getBoolean(key: string, fallback = false): Promise<boolean> {
  if (!isNative()) {
    const v = memDefaults[key];
    return typeof v === 'boolean' ? v : fallback;
  }
  try {
    const n = await getNative();
    const { value, isStatic } = await n.getBoolean(key);
    if (isStatic) {
      const d = memDefaults[key];
      return typeof d === 'boolean' ? d : fallback;
    }
    return value;
  } catch (err) {
    console.warn('RemoteConfig.getBoolean failed', key, err);
    return fallback;
  }
}

export function isFetched(): boolean {
  return fetched;
}
